import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CreditCard,
  Plus,
  Eye,
  EyeOff,
  Settings,
  ArrowUpRight,
  ArrowDownLeft,
  Download,
  MoreVertical,
  Lock,
  TrendingUp,
  Menu,
  Loader2,
  RefreshCw,
  Wallet,
  PiggyBank,
  Briefcase,
  LineChart,
  X,
  Copy,
  FileText,
  Send,
  Snowflake,
  AlertTriangle,
  CheckCircle,
  Edit3,
  Trash2,
  Share2,
  Bell,
  Shield
} from 'lucide-react'
import OpenAccountModal from '../components/OpenAccountModal'
import { AccountsAPI, TransactionsAPI, extractArray } from '../services/api'

const getAccountIcon = (category) => {
  switch (category?.toLowerCase()) {
    case 'savings':
      return <PiggyBank className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
    case 'business':
      return <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
    case 'investment':
      return <LineChart className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
    default:
      return <Wallet className="w-5 h-5 md:w-6 md:h-6 text-slate-600" />
  }
}

export default function AccountsPage({ onMenuClick }) {
  const { t, i18n } = useTranslation(['accounts', 'common'])
  const [showBalances, setShowBalances] = useState(true)
  const [accounts, setAccounts] = useState([])
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [recentTransactions, setRecentTransactions] = useState([])
  const [accountTypes, setAccountTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showOpenAccount, setShowOpenAccount] = useState(false)

  // New states for menu and modals
  const [activeMenu, setActiveMenu] = useState(null)
  const [showAccountSettings, setShowAccountSettings] = useState(false)
  const [showCloseAccountConfirm, setShowCloseAccountConfirm] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [notification, setNotification] = useState(null)
  const [editingAccountName, setEditingAccountName] = useState(false)
  const [newAccountName, setNewAccountName] = useState('')
  const menuRef = useRef(null)

  useEffect(() => {
    fetchAccounts()
  }, [])

  useEffect(() => {
    if (selectedAccount) {
      fetchAccountTransactions(selectedAccount.id)
    }
  }, [selectedAccount])

  const fetchAccounts = async () => {
    setLoading(true)
    setError(null)
    try {
      const [accountsRes, typesRes] = await Promise.all([
        AccountsAPI.getAll(),
        AccountsAPI.getTypes()
      ])

      if (accountsRes.success) {
        const accountData = extractArray(accountsRes, 'accounts')
        setAccounts(accountData)
        if (accountData.length > 0 && !selectedAccount) {
          setSelectedAccount(accountData[0])
        }
      }

      if (typesRes.success) {
        setAccountTypes(extractArray(typesRes))
      }
    } catch (err) {
      console.error('Error fetching accounts:', err)
      setError(t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const fetchAccountTransactions = async (accountId) => {
    try {
      const res = await TransactionsAPI.getAll({ accountId, limit: 5 })
      if (res.success) {
        setRecentTransactions(extractArray(res, 'transactions'))
      }
    } catch (err) {
      console.error('Error fetching transactions:', err)
    }
  }

  const handleOpenAccount = async (accountData) => {
    const res = await AccountsAPI.open(
      accountData.accountTypeId,
      accountData.accountName,
      accountData.initialDeposit || 0
    )
    if (res.success) {
      setShowOpenAccount(false)
      fetchAccounts()
    } else {
      throw new Error(res.message || 'Failed to open account')
    }
  }

  const totalBalance = accounts.reduce((sum, account) => sum + parseFloat(account.balance || 0), 0)

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return t('time.today')
    if (days === 1) return t('time.yesterday')
    return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 3000)
  }

  const copyAccountNumber = (accountNumber) => {
    navigator.clipboard.writeText(accountNumber)
    showNotification(t('messages.accountNumberCopied'))
    setActiveMenu(null)
  }

  const copyRoutingNumber = () => {
    navigator.clipboard.writeText('021000021')
    showNotification(t('messages.routingNumberCopied'))
    setActiveMenu(null)
  }

  const handleRenameAccount = async () => {
    if (!newAccountName.trim()) return
    setActionLoading(true)
    try {
      const res = await AccountsAPI.update(selectedAccount.id, newAccountName)
      if (res.success) {
        showNotification(t('messages.accountRenamed'))
        setEditingAccountName(false)
        fetchAccounts()
      } else {
        showNotification(res.message || t('messages.renameFailed'), 'error')
      }
    } catch (err) {
      showNotification(t('messages.renameFailed'), 'error')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCloseAccount = async () => {
    setActionLoading(true)
    try {
      const res = await AccountsAPI.close(selectedAccount.id)
      if (res.success) {
        showNotification(t('messages.accountClosed'))
        setShowCloseAccountConfirm(false)
        setShowAccountSettings(false)
        setSelectedAccount(null)
        fetchAccounts()
      } else {
        showNotification(res.message || t('errors.closeFailed'), 'error')
      }
    } catch (err) {
      showNotification(err.message || t('errors.closeFailed'), 'error')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDownloadStatement = (account) => {
    // Generate statement data
    const statementData = {
      accountName: account.account_name || account.type_name,
      accountNumber: account.account_number,
      balance: account.balance,
      availableBalance: account.available_balance,
      status: account.status,
      type: account.type_name,
      generatedAt: new Date().toISOString()
    }

    const blob = new Blob([JSON.stringify(statementData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `statement_${account.account_number?.slice(-4)}_${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
    showNotification(t('messages.statementDownloaded'))
    setActiveMenu(null)
  }

  const accountMenuOptions = [
    { icon: Copy, label: t('menu.copyAccountNumber'), action: (acc) => copyAccountNumber(acc.account_number) },
    { icon: Share2, label: t('menu.copyRoutingNumber'), action: () => copyRoutingNumber() },
    { icon: FileText, label: t('actions.downloadStatement'), action: (acc) => handleDownloadStatement(acc) },
    { icon: Send, label: t('menu.transferFunds'), action: () => { setActiveMenu(null); showNotification(t('messages.useTransferPage'), 'info') } },
    { divider: true },
    { icon: Settings, label: t('menu.accountSettings'), action: (acc) => { setSelectedAccount(acc); setShowAccountSettings(true); setActiveMenu(null) } },
  ]

  if (loading && accounts.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">{t('loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900">{t('title')}</h1>
                <p className="text-xs md:text-sm text-slate-500 mt-0.5 hidden sm:block">{t('manageSubtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={fetchAccounts}
                disabled={loading}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={() => setShowBalances(!showBalances)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-lg transition-all text-xs md:text-sm font-medium text-slate-700 justify-center"
              >
                {showBalances ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="hidden sm:inline">{showBalances ? t('hideBalances') : t('showBalances')}</span>
              </button>

              <button
                onClick={() => setShowOpenAccount(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-xs md:text-sm font-medium justify-center"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">{t('openAccount')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center mb-6">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchAccounts}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('common:tryAgain')}
            </button>
          </div>
        ) : (
          <>
            {/* Overview Card */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 text-white mb-6 md:mb-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
              <div className="relative">
                <p className="text-slate-400 text-xs md:text-sm font-medium mb-2">{t('totalBalance')}</p>
                <div className="flex items-baseline gap-3">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight notranslate" translate="no">
                    {showBalances ? `$${totalBalance.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}` : '••••••••'}
                  </h2>
                </div>
                <p className="text-slate-400 text-xs md:text-sm mt-2">{t('across', { count: accounts.length })}</p>
              </div>
            </div>

            {accounts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('noAccountsYet')}</h3>
                <p className="text-xs text-slate-500 mb-4">{t('openFirstAccount')}</p>
                <button
                  onClick={() => setShowOpenAccount(true)}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  {t('openAccount')}
                </button>
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                {/* Accounts List */}
                <div className="lg:col-span-2 space-y-3 md:space-y-4">
                  {accounts.map((account, index) => (
                    <motion.div
                      key={account.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => setSelectedAccount(account)}
                      className={`bg-white border-2 rounded-lg md:rounded-xl p-4 md:p-6 cursor-pointer transition-all hover:shadow-sm ${
                        selectedAccount?.id === account.id ? 'border-slate-900' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4 md:mb-6">
                        <div className="flex items-center gap-3 md:gap-4">
                          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 ${
                            account.category?.toLowerCase() === 'savings' ? 'bg-emerald-50' :
                            account.category?.toLowerCase() === 'business' ? 'bg-purple-50' :
                            account.category?.toLowerCase() === 'investment' ? 'bg-blue-50' :
                            'bg-slate-100'
                          }`}>
                            {getAccountIcon(account.category)}
                          </div>
                          <div>
                            <h3 className="text-base md:text-lg font-semibold text-slate-900">
                              {account.account_name || account.type_name}
                            </h3>
                            <p className="text-xs md:text-sm text-slate-500">
                              {account.type_name} • ••{account.account_number?.slice(-4)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${
                            account.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                            account.status === 'frozen' ? 'bg-blue-50 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              account.status === 'active' ? 'bg-emerald-600' :
                              account.status === 'frozen' ? 'bg-blue-600' :
                              'bg-slate-400'
                            }`}></div>
                            {account.status}
                          </span>
                          <div className="relative" ref={activeMenu === account.id ? menuRef : null}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setActiveMenu(activeMenu === account.id ? null : account.id)
                              }}
                              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-slate-400" />
                            </button>

                            {/* Dropdown Menu */}
                            <AnimatePresence>
                              {activeMenu === account.id && (
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                  transition={{ duration: 0.15 }}
                                  className="absolute right-0 top-full mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-2 overflow-hidden"
                                >
                                  {accountMenuOptions.map((option, idx) => (
                                    option.divider ? (
                                      <div key={idx} className="border-t border-slate-100 my-2" />
                                    ) : (
                                      <button
                                        key={idx}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          option.action(account)
                                        }}
                                        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
                                      >
                                        <option.icon className="w-4 h-4 text-slate-500" />
                                        <span className="text-sm text-slate-700">{option.label}</span>
                                      </button>
                                    )
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">{t('availableBalance')}</p>
                          <p className="text-2xl font-bold text-slate-900 notranslate" translate="no">
                            {showBalances
                              ? `$${parseFloat(account.available_balance || account.balance).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}`
                              : '••••••'}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-slate-500 mb-1">{t('currentBalance')}</p>
                          <p className="text-2xl font-bold text-slate-900 notranslate" translate="no">
                            {showBalances
                              ? `$${parseFloat(account.balance).toLocaleString(i18n.language, { minimumFractionDigits: 2 })}`
                              : '••••••'}
                          </p>
                        </div>
                      </div>

                      {(account.interest_rate || account.category === 'investment') && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          {account.interest_rate > 0 && (
                            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg text-sm font-semibold notranslate" translate="no">
                              <TrendingUp className="w-4 h-4" />
                              {account.interest_rate}% APY
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Right Sidebar */}
                <div className="space-y-6">
                  {/* Selected Account Details */}
                  {selectedAccount && (
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-slate-900 mb-5">{t('accountDetails')}</h3>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">{t('accountName')}</span>
                          <span className="text-sm font-medium text-slate-900">
                            {selectedAccount.account_name || selectedAccount.type_name}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">{t('accountNumber')}</span>
                          <span className="text-sm font-medium text-slate-900">
                            ••{selectedAccount.account_number?.slice(-4)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">{t('accountType')}</span>
                          <span className="text-sm font-medium text-slate-900">{selectedAccount.type_name}</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">{t('common:status')}</span>
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                            <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                            {t(`status.${selectedAccount.status}`)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">{t('opened')}</span>
                          <span className="text-sm font-medium text-slate-900">
                            {new Date(selectedAccount.created_at).toLocaleDateString(i18n.language, { month: 'short', year: 'numeric' })}
                          </span>
                        </div>

                        {selectedAccount.interest_rate > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-500">{t('interestRate')}</span>
                            <span className="text-sm font-medium text-emerald-600 notranslate" translate="no">{selectedAccount.interest_rate}% APY</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-6 space-y-2">
                        <button
                          onClick={() => handleDownloadStatement(selectedAccount)}
                          className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          {t('actions.downloadStatement')}
                        </button>

                        <button
                          onClick={() => {
                            setNewAccountName(selectedAccount.account_name || selectedAccount.type_name)
                            setShowAccountSettings(true)
                          }}
                          className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <Settings className="w-4 h-4" />
                          {t('menu.accountSettings')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Security */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Lock className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900">{t('security.title')}</h3>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed">
                      {t('security.description')}
                    </p>
                  </div>

                  {/* Recent Activity */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-slate-900 mb-4">{t('recentActivity')}</h3>

                    {recentTransactions.length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-4">{t('noRecentTransactions')}</p>
                    ) : (
                      <div className="space-y-3">
                        {recentTransactions.map((transaction) => {
                          const isCredit = parseFloat(transaction.amount) > 0
                          return (
                            <div key={transaction.id} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600'
                                }`}>
                                  {isCredit ? (
                                    <ArrowDownLeft className="w-4 h-4" />
                                  ) : (
                                    <ArrowUpRight className="w-4 h-4" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-slate-900">
                                    {transaction.description || transaction.merchant_name || 'Transaction'}
                                  </p>
                                  <p className="text-xs text-slate-500">{formatDate(transaction.transaction_date)}</p>
                                </div>
                              </div>

                              <p className={`text-xs font-semibold notranslate ${
                                isCredit ? 'text-emerald-600' : 'text-slate-900'
                              }`} translate="no">
                                {isCredit ? '+' : '-'}${Math.abs(parseFloat(transaction.amount)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Open Account Modal */}
      {showOpenAccount && (
        <OpenAccountModal
          onClose={() => setShowOpenAccount(false)}
          onOpenAccount={handleOpenAccount}
          accountTypes={accountTypes}
        />
      )}

      {/* Account Settings Modal */}
      <AnimatePresence>
        {showAccountSettings && selectedAccount && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAccountSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{t('settings.title')}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {selectedAccount.type_name} • ••{selectedAccount.account_number?.slice(-4)}
                  </p>
                </div>
                <button
                  onClick={() => setShowAccountSettings(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Account Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('settings.accountNickname')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('settings.enterNickname')}
                    />
                    <button
                      onClick={handleRenameAccount}
                      disabled={actionLoading || newAccountName === (selectedAccount.account_name || selectedAccount.type_name)}
                      className="px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      {t('common:save')}
                    </button>
                  </div>
                </div>

                {/* Account Information */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">{t('settings.accountInformation')}</h3>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t('accountNumber')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-slate-900 notranslate" translate="no">{selectedAccount.account_number}</span>
                      <button
                        onClick={() => copyAccountNumber(selectedAccount.account_number)}
                        className="p-1.5 hover:bg-slate-200 rounded transition-colors"
                      >
                        <Copy className="w-4 h-4 text-slate-500" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t('routingNumber')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-slate-900 notranslate" translate="no">021000021</span>
                      <button
                        onClick={copyRoutingNumber}
                        className="p-1.5 hover:bg-slate-200 rounded transition-colors"
                      >
                        <Copy className="w-4 h-4 text-slate-500" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t('accountType')}</span>
                    <span className="text-sm font-medium text-slate-900">{selectedAccount.type_name}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t('settings.openedDate')}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {new Date(selectedAccount.created_at).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>

                {/* Notifications Settings */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">{t('settings.notifications')}</h3>

                  <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-700">{t('settings.lowBalanceAlerts')}</span>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                  </label>

                  <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-700">{t('settings.largeTransactionAlerts')}</span>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500" />
                  </label>
                </div>

                {/* Danger Zone */}
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-semibold text-red-600 mb-3">{t('settings.dangerZone')}</h3>
                  <button
                    onClick={() => setShowCloseAccountConfirm(true)}
                    disabled={parseFloat(selectedAccount.balance) > 0}
                    className="w-full py-2.5 border-2 border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('settings.closeAccount')}
                  </button>
                  {parseFloat(selectedAccount.balance) > 0 && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      {t('settings.transferFundsFirst')}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Close Account Confirmation Modal */}
      <AnimatePresence>
        {showCloseAccountConfirm && selectedAccount && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setShowCloseAccountConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{t('confirmClose.title')}</h3>
                  <p className="text-sm text-slate-500">{t('confirmClose.cannotBeUndone')}</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 mb-6" dangerouslySetInnerHTML={{
                __html: t('confirmClose.confirmMessage', {
                  accountName: selectedAccount.account_name || selectedAccount.type_name,
                  lastFour: selectedAccount.account_number?.slice(-4)
                })
              }} />

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCloseAccountConfirm(false)}
                  className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleCloseAccount}
                  disabled={actionLoading}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('settings.closeAccount')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className={`fixed bottom-6 right-6 z-[70] px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${
              notification.type === 'error' ? 'bg-red-600 text-white' :
              notification.type === 'info' ? 'bg-blue-600 text-white' :
              'bg-slate-900 text-white'
            }`}
          >
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
